import * as XLSX from 'xlsx';
import * as XLSXStyle from 'xlsx-js-style';

export type DownloadExcelOptions = {
  /** Rows above the column header (title, building, kỳ, …) */
  metaRows?: (string | number)[][];
  /** 0-based indices within `metaRows` to render bold */
  boldMetaRowIndices?: number[];
  /** 0-based row indices in `rows` (excluding header) to render bold */
  boldRowIndices?: number[];
  /** 0-based column indices to right-align (header + all data rows) */
  rightAlignColumnIndices?: number[];
};

function applyRightAlignColumns(ws: XLSXStyle.WorkSheet, totalRows: number, columnIndices: number[]) {
  for (let row = 0; row < totalRows; row += 1) {
    for (const col of columnIndices) {
      const cellRef = XLSXStyle.utils.encode_cell({ r: row, c: col });
      const cell = ws[cellRef];
      if (!cell) continue;
      cell.s = {
        ...(cell.s ?? {}),
        alignment: { ...((cell.s as { alignment?: object })?.alignment ?? {}), horizontal: 'right' },
      };
    }
  }
}

function applyBoldRows(
  ws: XLSXStyle.WorkSheet,
  headerColumnCount: number,
  excelRowIndices: number[],
) {
  for (const excelRow of excelRowIndices) {
    for (let col = 0; col < headerColumnCount; col += 1) {
      const cellRef = XLSXStyle.utils.encode_cell({ r: excelRow, c: col });
      const cell = ws[cellRef];
      if (!cell) continue;
      cell.s = { ...(cell.s ?? {}), font: { ...((cell.s as { font?: object })?.font ?? {}), bold: true } };
    }
  }
}

export function downloadExcel(
  sheetName: string,
  headers: (string | number)[],
  rows: (string | number)[][],
  filename: string,
  options?: DownloadExcelOptions,
) {
  const metaRows = options?.metaRows ?? [];
  const useBold =
    (options?.boldRowIndices?.length ?? 0) > 0 ||
    (options?.boldMetaRowIndices?.length ?? 0) > 0;
  const useRightAlign = (options?.rightAlignColumnIndices?.length ?? 0) > 0;
  const useStyledSheet = useBold || useRightAlign;
  const sheetAoa = [...metaRows, headers, ...rows];
  const ws = useStyledSheet
    ? XLSXStyle.utils.aoa_to_sheet(sheetAoa)
    : XLSX.utils.aoa_to_sheet(sheetAoa);

  if (useStyledSheet) {
    const styledWs = ws as XLSXStyle.WorkSheet;
    const headerExcelRow = metaRows.length;
    const boldExcelRows: number[] = [];
    if (options?.boldMetaRowIndices?.length) {
      boldExcelRows.push(...options.boldMetaRowIndices);
    }
    if (options?.boldRowIndices?.length) {
      boldExcelRows.push(
        ...options.boldRowIndices.map((dataRowIndex) => headerExcelRow + 1 + dataRowIndex),
      );
    }
    if (boldExcelRows.length) {
      applyBoldRows(styledWs, headers.length, boldExcelRows);
    }
    if (options?.rightAlignColumnIndices?.length) {
      applyRightAlignColumns(styledWs, sheetAoa.length, options.rightAlignColumnIndices);
    }
  }

  const wb = useStyledSheet ? XLSXStyle.utils.book_new() : XLSX.utils.book_new();
  if (useStyledSheet) {
    XLSXStyle.utils.book_append_sheet(wb, ws, sheetName.substring(0, 31));
  } else {
    XLSX.utils.book_append_sheet(wb, ws, sheetName.substring(0, 31));
  }
  const buffer = useStyledSheet
    ? XLSXStyle.write(wb, { bookType: 'xlsx', type: 'array' })
    : XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

export function sanitizeExportFilename(name: string): string {
  return name.replace(/[^\w\u00C0-\u024F\u1E00-\u1EFF.-]+/g, '_').replace(/_+/g, '_');
}

/** Billing period in filenames, e.g. `09/2026` → `ky-09-2026`. */
export function billingPeriodToFilenameKy(collectionPeriod: string): string {
  const trimmed = collectionPeriod.trim();
  if (!trimmed) return 'ky-unknown';
  return `ky-${trimmed.replace(/\//g, '-')}`;
}

/** Project account filter segment for income-expense exports. */
export function projectAccountLabelForFilename(accountName?: string | null): string {
  const name = accountName?.trim();
  if (!name) return 'tat-ca-tai-khoan';
  return `tk-${sanitizeExportFilename(name)}`;
}
